import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Plus,
  Calendar,
  MapPin,
  Users,
  UserPlus,
  X,
  Search,
  Edit2,
  ChevronRight
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import EventForm from '../components/EventForm';
import ContactForm from '../components/ContactForm';
import { format } from 'date-fns';

const EVENT_TYPE_LABELS = {
  conference: 'Conference',
  meetup: 'Meetup',
  dinner: 'Dinner',
  webinar: 'Webinar',
  networking: 'Networking',
  other: 'Other'
};

const EVENT_TYPE_COLORS = {
  conference: '#3b82f6',
  meetup: '#22c55e',
  dinner: '#f59e0b',
  webinar: '#8b5cf6',
  networking: '#1e3a5f',
  other: '#6b7280'
};

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    fetchEvent,
    updateEvent,
    deleteEvent,
    fetchContact,
    contacts,
    fetchContacts,
    createContact
  } = useApp();

  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAttendeePicker, setShowAttendeePicker] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const eventData = await fetchEvent(id);
      if (!eventData) {
        navigate('/events');
        return;
      }
      setEvent(eventData);

      const attendeePromises = (eventData.attendee_ids || []).map(aid => fetchContact(aid));
      const attendeeData = await Promise.all(attendeePromises);
      setAttendees(attendeeData.filter(Boolean));
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  }, [id, fetchEvent, fetchContact, navigate]);

  useEffect(() => {
    loadData();
    fetchContacts();
  }, [id]);

  const handleDelete = async () => {
    try {
      await deleteEvent(id);
      navigate('/events');
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleEditSave = async (data) => {
    await updateEvent(id, data);
    await loadData();
  };

  const handleAddAttendee = async (contact) => {
    const newAttendeeIds = [...(event.attendee_ids || []), contact.id];
    await updateEvent(id, { attendee_ids: newAttendeeIds });
    await loadData();
    setAttendeeSearch('');
  };

  const handleRemoveAttendee = async (contactId) => {
    const newAttendeeIds = (event.attendee_ids || []).filter(aid => aid !== contactId);
    await updateEvent(id, { attendee_ids: newAttendeeIds });
    await loadData();
  };

  const handleNewContact = async (contactData) => {
    const newContact = await createContact(contactData);
    await fetchContacts();
    if (newContact?.id) {
      const newAttendeeIds = [...(event.attendee_ids || []), newContact.id];
      await updateEvent(id, { attendee_ids: newAttendeeIds });
      await loadData();
    }
    setShowContactForm(false);
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr >= today;
  };

  const filteredContacts = contacts.filter(c => {
    if ((event?.attendee_ids || []).includes(c.id)) return false;
    if (!attendeeSearch) return true;
    const searchLower = attendeeSearch.toLowerCase();
    const fullName = `${c.first_name} ${c.last_name || ''}`.toLowerCase();
    return fullName.includes(searchLower) ||
           (c.company && c.company.toLowerCase().includes(searchLower));
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!event) return null;

  const typeColor = EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => navigate('/events')}
          className="btn btn-ghost"
          style={{ marginBottom: '1rem', padding: '0.25rem 0.5rem' }}
        >
          <ArrowLeft size={18} />
          Back to Events
        </button>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '0.75rem',
              background: `${typeColor}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Calendar size={28} style={{ color: typeColor }} />
            </div>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                {event.title}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span
                  className="badge"
                  style={{
                    background: `${typeColor}15`,
                    color: typeColor
                  }}
                >
                  {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                </span>
                <span className="text-sm text-muted">
                  {formatEventDate(event.date)}
                </span>
                {isUpcoming(event.date) && (
                  <span className="badge" style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: 'var(--success)'
                  }}>
                    Upcoming
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowEditForm(true)}
            >
              <Edit2 size={16} />
              Edit
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setShowDeleteConfirm(true)}
              style={{ color: 'var(--danger)' }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: '1.5rem',
        alignItems: 'start'
      }} className="event-detail-grid">
        {/* Sidebar - Event Info */}
        <div className="card">
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--gray-500)',
            marginBottom: '1rem'
          }}>
            Event Details
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <div className="text-xs text-muted" style={{ marginBottom: '0.25rem' }}>Date</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} className="text-muted" />
                <span className="text-sm">{formatEventDate(event.date)}</span>
              </div>
            </div>

            {event.location && (
              <div>
                <div className="text-xs text-muted" style={{ marginBottom: '0.25rem' }}>Location</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MapPin size={16} className="text-muted" />
                  <span className="text-sm">{event.location}</span>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs text-muted" style={{ marginBottom: '0.25rem' }}>Type</div>
              <span
                className="badge"
                style={{
                  background: `${typeColor}15`,
                  color: typeColor
                }}
              >
                {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
              </span>
            </div>

            {event.description && (
              <div>
                <div className="text-xs text-muted" style={{ marginBottom: '0.25rem' }}>Description</div>
                <p className="text-sm" style={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  {event.description}
                </p>
              </div>
            )}

            <div>
              <div className="text-xs text-muted" style={{ marginBottom: '0.25rem' }}>Attendees</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={16} className="text-muted" />
                <span className="text-sm">
                  {attendees.length} attendee{attendees.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Attendees */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
              Attendees ({attendees.length})
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowAttendeePicker(!showAttendeePicker)}
              >
                <Plus size={16} />
                Add Attendee
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowContactForm(true)}
              >
                <UserPlus size={16} />
                New Contact
              </button>
            </div>
          </div>

          {/* Attendee picker */}
          {showAttendeePicker && (
            <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem' }}>
              <div className="search-wrapper" style={{ marginBottom: '0.5rem' }}>
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  value={attendeeSearch}
                  onChange={(e) => setAttendeeSearch(e.target.value)}
                  className="form-input search-input"
                  placeholder="Search contacts to add..."
                  autoFocus
                />
              </div>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid var(--gray-200)',
                borderRadius: '0.375rem'
              }}>
                {filteredContacts.length === 0 ? (
                  <div style={{
                    padding: '1rem',
                    textAlign: 'center',
                    color: 'var(--gray-500)',
                    fontSize: '0.875rem'
                  }}>
                    No contacts found
                  </div>
                ) : (
                  filteredContacts.slice(0, 10).map(contact => (
                    <button
                      key={contact.id}
                      onClick={() => handleAddAttendee(contact)}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--gray-100)',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <div className="avatar" style={{
                        width: '32px',
                        height: '32px',
                        fontSize: '0.75rem'
                      }}>
                        {contact.first_name[0]}{contact.last_name?.[0] || ''}
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {contact.first_name} {contact.last_name}
                        </div>
                        {contact.company && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                            {contact.company}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Attendee grid */}
          {attendees.length === 0 ? (
            <div className="card empty-state">
              <Users size={36} style={{ color: 'var(--gray-300)', marginBottom: '0.75rem' }} />
              <p className="text-sm text-muted">No attendees yet</p>
              <button
                className="btn btn-secondary btn-sm mt-3"
                onClick={() => setShowAttendeePicker(true)}
              >
                <Plus size={16} />
                Add Attendee
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '0.75rem'
            }}>
              {attendees.map(contact => (
                <div
                  key={contact.id}
                  className="card"
                  style={{
                    padding: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <Link
                    to={`/contacts/${contact.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flex: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                      minWidth: 0
                    }}
                  >
                    <div className="avatar" style={{
                      width: '40px',
                      height: '40px',
                      fontSize: '0.875rem',
                      flexShrink: 0
                    }}>
                      {contact.first_name[0]}{contact.last_name?.[0] || ''}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="font-medium" style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {contact.first_name} {contact.last_name || ''}
                      </div>
                      {contact.company && (
                        <div className="text-sm text-muted" style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {contact.company}
                        </div>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-muted" style={{ flexShrink: 0 }} />
                  </Link>
                  <button
                    onClick={() => handleRemoveAttendee(contact.id)}
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--gray-400)', flexShrink: 0 }}
                    title="Remove attendee"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Event</h2>
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete <strong>{event.title}</strong>?
                This will also remove all auto-generated interactions for this event's attendees.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit form modal */}
      {showEditForm && (
        <EventForm
          event={event}
          onClose={() => setShowEditForm(false)}
          onSave={handleEditSave}
        />
      )}

      {/* New contact form */}
      {showContactForm && (
        <ContactForm
          onClose={() => setShowContactForm(false)}
          onSave={handleNewContact}
        />
      )}

      <style>{`
        @media (max-width: 768px) {
          .event-detail-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

export default EventDetail;
